/**
 * Merchant Pro V1 PR-4a — TableActionSheet.
 *
 * Slide-over modal anchored to the right; lists contextually-correct
 * actions for the selected table based on its current state.
 *
 * Actions:
 *   • Block (state=available) — requires block_reason, optional blocked_until.
 *   • Unblock (state=blocked) — convenience POST.
 *   • Mark cleaning (state=available/checked_in/playing).
 *   • Mark playing (state=checked_in only — Mahjong manual escalation).
 *   • Mark available (state=cleaning/blocked).
 *
 * The 409 "table_has_active_reservation" surface is rendered as an inline
 * red banner (covers screenshot #5 in the closeout deliverable).
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Lock, Unlock, BrushCleaning, Gamepad2, Check, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { STATE_PALETTE } from '@/components/owner/ChairGridTableCard';
import RequirePermission, { usePermission } from '@/lib/capabilities';

const STATE_LABEL = {
  available:  'Available',
  reserved:   'Reserved',
  checked_in: 'Checked-In',
  playing:    'Playing',
  cleaning:   'Cleaning',
  blocked:    'Blocked',
};

// Sub-phase RBAC-C2 (2026-06-18) — capability tags per operator-action.
// Each action key maps to exactly one capability ID (recon §4 inventory).
// Note: per RBAC-C2 brief §B.1, ALL state transitions (mark available /
// cleaning / playing) share `floor_plan.set_state`. The granular
// `floor_plan.mark_cleaning` cap in the inventory is intentionally NOT
// used in V1 wraps — kept in the inventory for future granular gating.
// `block` form-submit also uses `floor_plan.block`.
const ACTION_CAP = {
  block:     'floor_plan.block',
  unblock:   'floor_plan.unblock',
  cleaning:  'floor_plan.set_state',
  playing:   'floor_plan.set_state',
  available: 'floor_plan.set_state',
};

function _genIdempotencyKey() {
  // 24-char hex-ish key — meets the 8-128 backend bound.
  const arr = new Uint8Array(12);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < 12; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

export default function TableActionSheet({ open, table, roomName, onClose, onApplied }) {
  const { token } = useAuthStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockedUntil, setBlockedUntil] = useState('');

  // Sub-phase RBAC-C2 — read effective caps once for the visible-count
  // computation. Wraps below are the authoritative gate; the count is
  // used only to render a friendly "no actions" empty-state copy when
  // every wrap returns null.
  const canBlock     = usePermission('floor_plan.block');
  const canUnblock   = usePermission('floor_plan.unblock');
  const canSetState  = usePermission('floor_plan.set_state');

  // Reset on table change.
  useEffect(() => {
    setActiveAction(null);
    setBlockReason('');
    setBlockedUntil('');
    setError(null);
    setBusy(false);
  }, [table?.id]);

  const state = table?.state || 'available';
  const stateColor = STATE_PALETTE[state] || STATE_PALETTE.available;

  // Available actions per state.
  const actions = useMemo(() => {
    const a = [];
    if (state === 'available') {
      a.push({ key: 'block',    label: 'Block table',    icon: Lock,            tone: 'red',    cap: ACTION_CAP.block });
      a.push({ key: 'cleaning', label: 'Mark cleaning',  icon: BrushCleaning,   tone: 'orange', cap: ACTION_CAP.cleaning });
    } else if (state === 'blocked') {
      a.push({ key: 'unblock',  label: 'Unblock table',  icon: Unlock,          tone: 'green',  cap: ACTION_CAP.unblock });
    } else if (state === 'cleaning') {
      a.push({ key: 'available', label: 'Mark available', icon: Check,          tone: 'green',  cap: ACTION_CAP.available });
    } else if (state === 'checked_in') {
      a.push({ key: 'playing',  label: 'Start playing',  icon: Gamepad2,        tone: 'violet', cap: ACTION_CAP.playing });
      a.push({ key: 'cleaning', label: 'Mark cleaning',  icon: BrushCleaning,   tone: 'orange', cap: ACTION_CAP.cleaning });
    } else if (state === 'playing') {
      a.push({ key: 'cleaning', label: 'Mark cleaning',  icon: BrushCleaning,   tone: 'orange', cap: ACTION_CAP.cleaning });
    } else if (state === 'reserved') {
      // No operator actions — wait for check-in / cancel / lifecycle.
    }
    return a;
  }, [state]);

  // Sub-phase RBAC-C2 — does the actor have ANY of the per-state caps?
  // Used to swap the "operator actions" header into a polite empty
  // state when the actions array is non-empty but every wrap hides.
  const hasAnyVisibleAction = actions.some((a) => {
    if (a.cap === 'floor_plan.block')     return canBlock;
    if (a.cap === 'floor_plan.unblock')   return canUnblock;
    if (a.cap === 'floor_plan.set_state') return canSetState;
    return false;
  });

  if (!open || !table) return null;

  const applyAction = async (key) => {
    setError(null);
    setActiveAction(key);

    // Block requires a reason — defer to form submission.
    if (key === 'block') return;

    setBusy(true);
    try {
      if (key === 'unblock') {
        await api.cashierTableUnblock(token, table.id, { idempotency_key: _genIdempotencyKey() });
      } else {
        await api.ownerTableStatePatch(token, table.id, {
          state: key, // available / cleaning / playing
          idempotency_key: _genIdempotencyKey(),
        });
      }
      await onApplied();
    } catch (e) {
      setError(e?.raw?.detail || e?.detail || e?.message || 'Action failed');
      setActiveAction(null);
    } finally {
      setBusy(false);
    }
  };

  const submitBlock = async (ev) => {
    ev?.preventDefault?.();
    if (!blockReason.trim()) {
      setError({ code: 'block_reason_required', message: 'Please provide a reason.' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        state: 'blocked',
        block_reason: blockReason.trim(),
        idempotency_key: _genIdempotencyKey(),
      };
      if (blockedUntil) {
        body.blocked_until = new Date(blockedUntil).toISOString();
      }
      await api.ownerTableStatePatch(token, table.id, body);
      await onApplied();
    } catch (e) {
      setError(e?.raw?.detail || e?.detail || e?.message || 'Block failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="table-action-sheet-overlay"
      className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <aside
        data-testid="table-action-sheet"
        className="w-full max-w-md h-full bg-[#0E0E13] border-l border-white/10 shadow-2xl flex flex-col"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">
              {roomName ?? 'Table'}
            </div>
            <h2 className="text-lg font-semibold mt-0.5" data-testid="table-action-sheet-title">
              {table.name || table.id}
            </h2>
            <div className="flex items-center gap-2 mt-2">
              <span
                className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                style={{ background: stateColor + '22', color: stateColor, border: `1px solid ${stateColor}55` }}
                data-testid="table-action-sheet-current-state"
              >
                {STATE_LABEL[state] ?? state}
              </span>
              <span className="text-xs text-white/55">
                {Number(table.current_party_size || 0)} / {Number(table.capacity || 0)} seats
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="table-action-sheet-close"
            className="p-2 rounded-full text-white/70 hover:bg-white/5"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Block-reason form */}
          {activeAction === 'block' ? (
            <form
              onSubmit={submitBlock}
              data-testid="table-action-sheet-block-form"
              className="space-y-3 rounded-2xl border border-red-500/30 bg-red-500/5 p-4"
            >
              <div>
                <label className="text-xs uppercase tracking-widest text-red-300/90 font-semibold">
                  Block reason · required
                </label>
                <textarea
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  data-testid="table-action-sheet-block-reason"
                  rows={3}
                  required
                  maxLength={200}
                  className="mt-1 w-full px-3 py-2 rounded-2xl bg-[#15151B] border border-white/10 text-sm text-white/90 focus:outline-none focus:ring-2 focus:ring-red-500/60"
                  placeholder="e.g., Equipment failure · Maintenance · VIP hold"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/55 font-semibold">
                  Auto-unblock at · optional
                </label>
                <input
                  type="datetime-local"
                  value={blockedUntil}
                  onChange={(e) => setBlockedUntil(e.target.value)}
                  data-testid="table-action-sheet-blocked-until"
                  className="mt-1 w-full px-3 py-2 rounded-2xl bg-[#15151B] border border-white/10 text-sm text-white/90 focus:outline-none focus:ring-2 focus:ring-red-500/60"
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setActiveAction(null); setError(null); }}
                  className="px-3 py-2 rounded-2xl text-sm text-white/70 hover:bg-white/5 border border-white/10"
                  data-testid="table-action-sheet-block-cancel"
                  disabled={busy}
                >
                  Cancel
                </button>
                <RequirePermission cap={ACTION_CAP.block}>
                  <button
                    type="submit"
                    disabled={busy || !blockReason.trim()}
                    data-testid="table-action-sheet-block-submit"
                    className="px-4 py-2 rounded-2xl text-sm font-semibold bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-50"
                  >
                    {busy ? 'Blocking…' : 'Confirm block'}
                  </button>
                </RequirePermission>
              </div>
            </form>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">
                Operator actions
              </div>
              {actions.length === 0 || !hasAnyVisibleAction ? (
                <div
                  data-testid="table-action-sheet-no-actions"
                  className="rounded-2xl border border-white/10 bg-[#15151B] p-4 text-sm text-white/55"
                >
                  {actions.length === 0 ? (
                    <>
                      No operator actions available while the table is{' '}
                      <span style={{ color: stateColor }}>{STATE_LABEL[state] ?? state}</span>.
                      Wait for the reservation to advance, or use the reservation detail
                      page to move the booking to a different table.
                    </>
                  ) : (
                    <>
                      Your role does not have permission to change this table&apos;s state.
                      Contact your Brand Owner for access.
                    </>
                  )}
                </div>
              ) : null}
              {actions.map((a) => {
                const Icon = a.icon;
                const toneClass = {
                  red:    'border-red-500/40 hover:bg-red-500/10 text-red-200',
                  green:  'border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-200',
                  orange: 'border-orange-500/40 hover:bg-orange-500/10 text-orange-200',
                  violet: 'border-violet-500/40 hover:bg-violet-500/10 text-violet-200',
                }[a.tone] || 'border-white/10 hover:bg-white/5 text-white/85';
                return (
                  <RequirePermission key={a.key} cap={a.cap}>
                    <button
                      type="button"
                      onClick={() => applyAction(a.key)}
                      disabled={busy}
                      data-testid={`table-action-sheet-action-${a.key}`}
                      className={`w-full flex items-center gap-3 rounded-2xl border bg-[#15151B] px-4 py-3 text-sm font-medium transition-colors disabled:opacity-50 ${toneClass}`}
                    >
                      <Icon size={16} />
                      {a.label}
                    </button>
                  </RequirePermission>
                );
              })}
            </>
          )}

          {error ? (
            <div
              data-testid="table-action-sheet-error"
              className="flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-4"
            >
              <AlertTriangle size={16} className="mt-0.5 text-red-300 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold text-red-200">
                  {(error.code || 'Action failed').toString()}
                </div>
                <div className="text-red-100/85 mt-0.5">
                  {typeof error === 'string' ? error : (error.message || 'Please retry or refresh the floor plan.')}
                </div>
              </div>
            </div>
          ) : null}

          {/* Block-state metadata */}
          {state === 'blocked' ? (
            <div className="rounded-2xl border border-white/10 bg-[#15151B] p-4 space-y-2">
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold">
                Current block details
              </div>
              {table.block_reason ? (
                <div className="text-sm">
                  <span className="text-white/55">Reason: </span>
                  <span className="text-white/90">{table.block_reason}</span>
                </div>
              ) : null}
              {table.blocked_until ? (
                <div className="text-sm">
                  <span className="text-white/55">Auto-unblock at: </span>
                  <span className="text-white/90 font-mono">
                    {new Date(table.blocked_until).toLocaleString()}
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
